import { useEffect, useRef, useState } from 'preact/hooks';
import type { LibraryFeedbackBinding, ServerClient, StudentLibraryRating } from './api.js';

/** Optional, authenticated feedback lives entirely outside generated code. */
export function StudentReleaseFeedback({api, binding}: {api: ServerClient; binding: LibraryFeedbackBinding}) {
  const [eligible, setEligible] = useState(false);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<StudentLibraryRating>({fun: null, helpfulness: null});
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    setEligible(false); setOpen(false); setSaved(false); setError('');
    api.studentLibraryFeedback(binding.mountId).then(r => {
      if (!active || !r.eligible || r.releaseId !== binding.releaseId) return;
      setEligible(true);
      if (r.rating) { setRating(r.rating); setSaved(r.rating.fun !== null || r.rating.helpfulness !== null); }
    }).catch(() => { /* Optional feedback must never interrupt the activity. */ });
    return () => { active = false; };
  }, [api, binding.mountId, binding.releaseId]);
  useEffect(() => { if (open) panel.current?.querySelector<HTMLButtonElement>('button')?.focus(); }, [open]);
  const close = () => { setOpen(false); trigger.current?.focus(); };
  const submit = async () => {
    if (busy || (rating.fun === null && rating.helpfulness === null)) return;
    setBusy(true); setError('');
    try { const result = await api.rateLibraryRelease(binding.mountId, rating); setRating(result.rating); setSaved(true); }
    catch { setError('Your feedback could not be saved. Try again, or skip it.'); }
    finally { setBusy(false); }
  };
  if (!eligible) return null;
  return <div class="release-feedback">
    <button class="quiet" type="button" ref={trigger} aria-expanded={open} aria-controls="release-feedback-panel" onClick={() => open ? close() : setOpen(true)}>Feedback</button>
    {open ? <div id="release-feedback-panel" class="release-feedback-panel" ref={panel} role="region" aria-labelledby="release-feedback-title" onKeyDown={e => { e.stopPropagation(); if (e.key === 'Escape') { e.preventDefault(); close(); } }}>
      <div class="class-head"><h2 id="release-feedback-title">How was this activity?</h2><button class="quiet" type="button" onClick={close}>Close</button></div>
      <p>Optional feedback for the version you opened. It never affects grades or rewards.</p>
      <form onSubmit={e => { e.preventDefault(); void submit(); }}>
        {(['fun', 'helpfulness'] as const).map(dimension => <fieldset class="feedback-scale" key={dimension} disabled={busy}>
          <legend>{dimension === 'fun' ? 'How fun was it?' : 'How helpful was it for learning?'}</legend>
          <p class="feedback-scale-note">1 = {dimension === 'fun' ? 'not fun' : 'not helpful'}, 5 = {dimension === 'fun' ? 'very fun' : 'very helpful'}</p>
          <div class="feedback-values">{[1, 2, 3, 4, 5].map(value => <label key={value}>
            <input type="radio" name={dimension} value={value} checked={rating[dimension] === value} onChange={() => { setRating(r => ({...r, [dimension]: value})); setSaved(false); }} /><span>{value}</span>
          </label>)}</div>
          <button class="quiet" type="button" onClick={() => { setRating(r => ({...r, [dimension]: null})); setSaved(false); }}>Skip this question</button>
        </fieldset>)}
        {error ? <p role="alert">{error}</p> : null}
        {saved ? <p role="status">Your feedback is saved. You can update it.</p> : null}
        <div class="library-actions"><button class="act" type="submit" disabled={busy || (rating.fun === null && rating.helpfulness === null)}>{busy ? 'Saving…' : saved ? 'Update feedback' : 'Save feedback'}</button><button class="quiet" type="button" onClick={close}>Maybe later</button></div>
      </form>
    </div> : null}
  </div>;
}
